// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DataTableContext from "@stoneage/MobiusOverrides/DataTable/DataTableContext";
import { color } from "@stoneage/XDL/Utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type DataTableRowProps = MobiusStyledComponentProps<
    "tr",
    {
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<DataTableRowProps>;
        evenRowCss?: StyledProp<DataTableRowProps>;
        /**
         * Indicates how the `css` property is combined with the default `css` property from the theme.
         * If true, the default css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
         */
        mergeCss?: boolean;
    }
>;

const DataTableRowStyle = styled.tr<DataTableRowProps>`
    background: ${getColor("common.background")};
    &:nth-child(even) {
        background: ${getColor("common.accent")};
        ${({ evenRowCss }) => evenRowCss}
    }
    ${injectCss}
`;

// XNG-Changes
const MobileDataTableRowStyle = styled.div<DataTableRowProps>`
    display: flex;
    flex-direction: column;
    background: ${getColor("common.background")};
    padding: 8px 12px;
    row-gap: 16px;
    &:first-child {
        border-top: 4px solid ${color("common.ui7")};
    }
    &:nth-child(even) {
        background: ${getColor("common.ui4")};
        ${({ evenRowCss }) => evenRowCss}
    }
    ${injectCss}
`;

const DataTableRow: React.FC<DataTableRowProps> = ({ mergeCss, css: dataTableCss = "", ...otherProps }) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;

    // XNG-Changes: add MobileDataTableRowStyle
    return (
        <DataTableContext.Consumer>
            {({ isMobile }) =>
                isMobile ? (
                    <MobileDataTableRowStyle
                        css={css`
                            ${applyStyledProp(["cssOverrides", "row"], resolvedMergeCss)}
                            ${dataTableCss}
                        `}
                        evenRowCss={applyStyledProp(["cssOverrides", "evenRow"], resolvedMergeCss)}
                        {...otherProps}
                    />
                ) : (
                    <DataTableRowStyle
                        css={css`
                            ${applyStyledProp(["cssOverrides", "row"], resolvedMergeCss)}
                            ${dataTableCss}
                        `}
                        evenRowCss={applyStyledProp(["cssOverrides", "evenRow"], resolvedMergeCss)}
                        {...otherProps}
                    />
                )
            }
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableRow);
